import React from "react";
import { Typography } from "antd";

import { W0, W1 } from "@components/Constants";

import CodeBlock from "./CodeBlock";
import { type Specification } from "./helpers/getSpecification";

const DESCRIPTION = "This file is the Swagger 2.0 specification of the API of this key.";
const DESCRIPTION_HOST = "Set the host of your client to";

const LABEL_FILE = "Specification file";
const LABEL_URL = "Specification URL";
const LABEL_SANDBOX = "API sandbox";
const LINK_SANDBOX = "Open the API sandbox";

const LABEL_STEP = "Step";
const LABEL_STEP_AGENT = "Give the specification to a coding agent";
const DESCRIPTION_STEP_AGENT = "Paste the URL into the agent, or add the file to the project. The agent can then write the calls that agree with this API.";

const LABEL_STEP_TYPES = "Make the types of your client";
const DESCRIPTION_STEP_TYPES = "Point a generator at the URL. Then set the base URL of the client to the domain of the organization.";

const FIRST_STEP_NUMBER = 1;

type Step = {
  title: string;
  description: string;
  command?: string;
};

type Props = {
  sandboxUrl?: string;
  specification: Specification;
};

const getTypesCommand = (url: string): string => {
  const command = `npx @hey-api/openapi-ts -i ${url} -o src/api`;

  return command;
};

const getSteps = (url: string): Step[] => {
  const typesCommand = getTypesCommand(url);

  const steps = [
    {
      title: LABEL_STEP_AGENT,
      description: DESCRIPTION_STEP_AGENT,
    },
    {
      title: LABEL_STEP_TYPES,
      description: DESCRIPTION_STEP_TYPES,
      command: typesCommand,
    },
  ];

  return steps;
};

const OrganizationPublicKeySpecification = ({
  sandboxUrl,
  specification,
}: Props): JSX.Element => {
  const {
    url,
    host,
    title,
    fileName,
  } = specification;

  const steps = getSteps(url);

  const hasSandboxUrl = !!sandboxUrl;

  const labelStyle = {
    marginBottom: W0 / 2,
  };

  const valueStyle = {
    marginBottom: W1,
    wordBreak: "break-all" as const,
  };

  const stepStyle = {
    marginBottom: W1,
  };

  const titleStyle = {
    marginBottom: W0 / 2,
  };

  const renderStep = (
    { title: stepTitle, description, command }: Step,
    index: number,
  ) => {
    const stepNumber = index + FIRST_STEP_NUMBER;
    const heading = `${LABEL_STEP} ${stepNumber}: ${stepTitle}`;
    const hasCommand = !!command;

    return (
      <div key={stepTitle} style={stepStyle}>
        <Typography.Paragraph style={titleStyle}>
          {heading}
        </Typography.Paragraph>

        <Typography.Paragraph type="secondary" style={titleStyle}>
          {description}
        </Typography.Paragraph>

        {hasCommand && (
          <CodeBlock isCopyable code={command} />
        )}
      </div>
    );
  };

  return (
    <>
      <Typography.Paragraph type="secondary">
        {DESCRIPTION}

        {" "}

        {DESCRIPTION_HOST}

        {" "}

        <Typography.Text code copyable={{ text: host }}>
          {host}
        </Typography.Text>

        .
      </Typography.Paragraph>

      <Typography.Paragraph type="secondary" style={labelStyle}>
        {LABEL_FILE}
      </Typography.Paragraph>

      <Typography.Paragraph style={valueStyle}>
        <Typography.Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {fileName}
        </Typography.Link>

        {` (${title})`}
      </Typography.Paragraph>

      <Typography.Paragraph type="secondary" style={labelStyle}>
        {LABEL_URL}
      </Typography.Paragraph>

      <Typography.Paragraph
        code
        copyable={{ text: url }}
        style={valueStyle}
      >
        {url}
      </Typography.Paragraph>

      {hasSandboxUrl && (
        <>
          <Typography.Paragraph type="secondary" style={labelStyle}>
            {LABEL_SANDBOX}
          </Typography.Paragraph>

          <Typography.Paragraph style={valueStyle}>
            <Typography.Link
              href={sandboxUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {LINK_SANDBOX}
            </Typography.Link>
          </Typography.Paragraph>
        </>
      )}

      {steps.map(renderStep)}
    </>
  );
};

export default OrganizationPublicKeySpecification;
